/*
 * home-table-datasource.ts
 * UTMS-Hcp-Dashboard
 * 
 * Created by Tetiana Makovii on 31.03.2021
 * Copyright © 2026 Sonova AG. All rights reserved.
 */

import { DataSource } from '@angular/cdk/collections';
import { Observable, BehaviorSubject, Subscription, first, skip } from 'rxjs';
import { UserRolesEnum } from 'src/app/common/enums/user-roles-enum';
import { UserListItem } from 'src/app/common/models/users-data/user-list-item.model';
import { UserListService } from 'src/app/services/user-list.service';
import { SortingHeadersEnum } from './enums/sorting-headers-enum';
import { SortingKeysEnum } from './enums/sorting-keys-enum';
import { SortingDirectionsEnum } from 'src/common-panel/enums/sorting-directions-enum';
import { COUNTRIES_IDS, CountryId } from 'src/common-panel/constants/country-configs.constants';


export class HomeTableDataSource extends DataSource<UserListItem>
{
  subscription: Subscription = new Subscription()

  private _data: UserListItem[] = []
  private _sortDirection = this.userListService.tableSettings.sortDirection
  private _sortingHeader = this.userListService.tableSettings.sortingHeader

  private _roleFilterState = this.userListService.tableSettings.roleFilterState
  private _countryFilterState = this.userListService.tableSettings.countryFilterState

  private _pageSize: number = this.userListService.tableSettings.pageSize;
  private _pageIndex: number = this.userListService.tableSettings.pageIndex;

  private usersSubject = new BehaviorSubject<UserListItem[]>([]);
  private loadingSubject = new BehaviorSubject<boolean>(false)

  public loading$ = this.loadingSubject.asObservable()

  get count()
  {
    return this._data.length
  }

  get data()
  {
    return this._data
  }

  set sortDirection(value: SortingDirectionsEnum)
  {
    this._sortDirection = value
    this.userListService.tableSettings.sortDirection = value
  }

  get sortDirection(): SortingDirectionsEnum
  {
    return this._sortDirection
  }

  set sortingHeader(value: SortingHeadersEnum)
  {
    this._sortingHeader = value
    this.userListService.tableSettings.sortingHeader = value
  }

  get sortingHeader(): SortingHeadersEnum
  {
    return this._sortingHeader
  }

  set roleFilter(state: UserRolesEnum | undefined)
  {
    this._roleFilterState = state
    this.userListService.tableSettings.roleFilterState = state
  }

  get roleFilter(): UserRolesEnum | undefined
  {
    return this._roleFilterState
  }
  set countryFilter(state: CountryId | undefined)
  {
    this._countryFilterState = state
    this.userListService.tableSettings.countryFilterState = state
  }

  get countryFilter(): CountryId | undefined
  {
    return this._countryFilterState
  }


  set pageSize(value: number)
  {
    this._pageSize = value
    this.userListService.tableSettings.pageSize = value
  }

  get pageSize(): number
  {
    return this._pageSize
  }

  set pageIndex(value: number)
  {
    this._pageIndex = value
    this.userListService.tableSettings.pageIndex = value
  }

  get pageIndex(): number
  {
    return this._pageIndex
  }

  constructor(private userListService: UserListService)
  {
    super();
  }

  /**
   * Connect this data source to the table. The table will only update when
   * the returned stream emits new items.
   * @returns A stream of the items to be rendered.
   */

  connect(): Observable<UserListItem[]>
  {
    return this.usersSubject.asObservable();
  }

  /**
   *  Called when the table is being destroyed. Use this function, to clean up
   * any open connections or free any held resources that were set up during connect.
   */

  disconnect()
  {
    this.usersSubject.complete();
    this.loadingSubject.complete();
    this.subscription.unsubscribe()
  }


  loadUsers()
  {
    this.loadingSubject.next(true)
    this.usersSubject.next([])
    if (this.sortDirection != SortingDirectionsEnum.none) this.sortData();
    this.filterData()
    let l_sub = this.userListService.loadData().pipe(first(val => val == true)).subscribe(() =>
    {
      this._data = this.userListService.data.slice()
      if (this.sortDirection != SortingDirectionsEnum.none) this.sortData();

      this.loadingSubject.next(false)
      this.filterBySearch();
      this.filterData()
      this.loadUsersPage();

    })


    let l_sub1 = this.userListService.filterTableByEmail.pipe(skip(1)).subscribe(l_userEmail =>
    {
      this.filterData()
      if (l_userEmail.length > 0)
      {
        this._data = this._data.filter(l_user => l_user.email?.toLowerCase().startsWith(l_userEmail.toLowerCase()))
      }
      this.loadUsersPage();
    })

    let l_sub2 = this.userListService.filterTableByUserName.pipe(skip(1)).subscribe(l_userName =>
    {
      this.filterData()
      if (l_userName.length > 0)
      {
        this._data = this._data.filter(l_user => l_user.firstName?.toLowerCase().startsWith(l_userName.toLowerCase()) || l_user.lastName?.toLowerCase().startsWith(l_userName.toLowerCase()) || (l_user.firstName?.toLowerCase() + ' ' + l_user.lastName?.toLowerCase()).startsWith(l_userName.toLowerCase()))
      }
      this.loadUsersPage();
    })

    let l_sub3 = this.userListService.filterTableByClinicId.pipe(skip(1)).subscribe(l_clinicId =>
    {
      this.filterData()
      this._data = this._data.filter(l_user => l_user.clinicId?.toLowerCase().startsWith(l_clinicId.toLowerCase()))

      this.loadUsersPage();
    })

    this.subscription.add(l_sub)
    this.subscription.add(l_sub1)
    this.subscription.add(l_sub2)
    this.subscription.add(l_sub3)
  }


  loadUsersPage()
  {
    let l_result = this._data.slice(this.pageIndex * this.pageSize, (this.pageIndex + 1) * this.pageSize)
    this.usersSubject.next(l_result)
  }


  sortData()
  {
    if (this.sortDirection == SortingDirectionsEnum.none)
    {
      this._data = this._data.sort(() => Math.random() - 0.5)
    }
    else
    {
      switch (this.sortingHeader)
      {
        case SortingHeadersEnum.name:
          {
            this.simpleSort(SortingKeysEnum.lastName);
            break;
          }
        case SortingHeadersEnum.email:
          {
            this.emailSort();
            break;
          }
        case SortingHeadersEnum.clinicId:
          {
            this.simpleSort(SortingKeysEnum.clinicId);
            break;
          }
        case SortingHeadersEnum.country:
          {
            this.simpleSort(SortingKeysEnum.country);
            break;
          }
        case SortingHeadersEnum.role:
          {
            this.simpleSort(SortingKeysEnum.role);
            break;
          }
        case SortingHeadersEnum.status:
          {
            this.statusSort();
            break;
          }


      }
    }
  }



  simpleSort(key: string)
  {
    let l_returnTrue = 1
    let l_returnFalse = -1

    if (this.sortDirection == SortingDirectionsEnum.desc)
    {
      l_returnTrue = -1
      l_returnFalse = 1
    }

    this._data.sort((obj1, obj2) =>
    {
      if (Object.keys(obj1).includes(key))
      {
        if (!(obj1 as any)[key] || (obj1 as any)[key] == "–")
          {
              return 1
          }

          if (!(obj2 as any)[key] || (obj2 as any)[key] == "–")
          {
              return -1
          }
        if ((obj1 as any)[key] > (obj2 as any)[key])
        {
          return l_returnTrue;
        }

        if ((obj1 as any)[key] < (obj2 as any)[key])
        {
          return l_returnFalse;
        }
      } else
      {
        throw new Error(`Key ${ key } does not found in keys of object of type UserListItem`)
      }
      return 0;
    });
  }

  emailSort()
  {
    let l_returnTrue = 1
    let l_returnFalse = -1
    if (this.sortDirection == SortingDirectionsEnum.desc)
    {
      l_returnTrue = -1
      l_returnFalse = 1
    }

    this._data.sort((obj1, obj2) =>
    {

      if (!obj1.email || obj1.email == "–")
      {
        return 1
      }

      if (!obj2.email || obj2.email == "–")
      {
        return -1
      }

      let l_emailObj1 = obj1.email.split('@')[0]
      let l_emailObj2 = obj2.email.split('@')[0]
      if (l_emailObj1.toLowerCase() > l_emailObj2.toLowerCase())
      {
        return l_returnTrue;
      }

      if (l_emailObj1.toLowerCase() < l_emailObj2.toLowerCase())
      {
        return l_returnFalse;
      }

      return 0;
    });
  }

  statusSort()
  {
    let l_returnTrue = 1
    let l_returnFalse = -1
    if (this.sortDirection == SortingDirectionsEnum.desc)
    {
      l_returnTrue = -1
      l_returnFalse = 1
    }

    this._data.sort((obj1, obj2) =>
    {
      if (!obj1.status)
      {
        return 1
      }

      if (!obj2.status)
      {
        return -1
      }
      
      if (obj1.status > obj2.status)
      {
        return l_returnTrue;
      }

      if (obj1.status < obj2.status)
      {
        return l_returnFalse;
      }
      if (!obj1.statusDate)
      {
        return l_returnTrue
      }

      if (!obj2.statusDate)
      {
        return l_returnFalse
      }
      if (obj1.statusDate < obj2.statusDate)
      {
        return l_returnTrue;
      }

      if (obj1.statusDate > obj2.statusDate)
      {
        return l_returnFalse;
      }
      return 0;
    });
  }


  filterBySearch()
  {
    if (this.userListService.filterTableByEmail.value.length > 0) this._data = this._data.filter(l_user => l_user.email?.toLowerCase().startsWith(this.userListService.filterTableByEmail.value.toLowerCase()))
    if (this.userListService.filterTableByUserName.value.length > 0) this._data = this._data.filter(l_user => l_user.firstName?.toLowerCase().startsWith(this.userListService.filterTableByUserName.value.toLowerCase())
      || l_user.lastName?.toLowerCase().startsWith(this.userListService.filterTableByUserName.value.toLowerCase())
      || (l_user.firstName?.toLowerCase() + ' ' + l_user.lastName?.toLowerCase()).startsWith(this.userListService.filterTableByUserName.value.toLowerCase()))
    if (this.userListService.filterTableByClinicId.value.length > 0) this._data = this._data.filter(l_user => l_user.clinicId?.toLowerCase().startsWith(this.userListService.filterTableByClinicId.value.toLowerCase()))

  }


  filterData()
  {
    this._data = this.userListService.data;
    //todo filter with subscription filtration in patient list screen datasource

    switch (this.roleFilter)
    {
      case UserRolesEnum.hcp:
        {
          this._data = this._data.filter(item => item.role === UserRolesEnum.hcp)
          break;
        }
      case UserRolesEnum.ent:
        {
          this._data = this._data.filter(item => item.role === UserRolesEnum.ent)
          break;
        }
      case UserRolesEnum.dal:
        {
          this._data = this._data.filter(item => item.role === UserRolesEnum.dal)
          break;
        }
      default: { break; }
    }

    if (this.countryFilter && Object.values(COUNTRIES_IDS).includes(this.countryFilter))
    {
      this._data = this._data.filter(item => item.countryId === this.countryFilter)
    }
    this.filterBySearch()
  }
}
